"use client";

import { useEffect, useState, type RefObject } from "react";
import { create } from "zustand";

interface SidebarInsetStore {
  /**
   * Pixels of the map's left edge the sidebar currently covers — zero when it is
   * closed, and zero when there is no sidebar at all.
   */
  left: number;
  setLeft: (left: number) => void;
}

/**
 * How much of the map the sidebar occludes.
 *
 * One resolved number rather than a width plus an open flag: the open state
 * already lives in `useSidebar`, and holding it twice is how the legacy app ended
 * up measuring the same panel two ways that disagreed.
 *
 * Read by `useCameraPadding`, which turns it into the map's camera padding, and by
 * `useVisibleArea`, which frames fits around it.
 */
export const useSidebarInset = create<SidebarInsetStore>((set) => ({
  left: 0,
  setLeft: (left) => set((state) => (state.left === left ? state : { left })),
}));

/**
 * Publish the sidebar's occlusion from the sidebar itself.
 *
 * Measured rather than derived from the width classes: the panel is `400px`,
 * then a third of the viewport, then `700px`, so a constant would have to mirror
 * the Tailwind config and would drift from it. A `ResizeObserver` is what makes a
 * plain window resize — which changes a proportional width with no toggle —
 * reach the map; the legacy app only ever re-read this on toggle.
 *
 * The observed width is the panel's own, which does not change when it collapses:
 * it slides out on a negative margin. `isOpen` is what turns that width into an
 * inset.
 */
export function usePublishSidebarInset(
  ref: RefObject<HTMLElement | null>,
  isOpen: boolean
): void {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => setWidth(element.offsetWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);

  useEffect(() => {
    useSidebarInset.getState().setLeft(isOpen ? width : 0);
  }, [isOpen, width]);

  // Mount-scoped, so a toggle doesn't flicker through zero on its way to the new
  // value: the desktop layout is dropped wholesale at the mobile breakpoint, and
  // a sidebar that no longer exists occludes nothing.
  useEffect(() => () => useSidebarInset.getState().setLeft(0), []);
}
